import { Box, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { RespondentRequestEntity } from 'src/api/useRespondentRequestApi';
import { SurveyInformationModal } from 'src/components/app-components/bonsai/survey-information/components/SurveyInformationModal';
import { StyledButton } from 'src/components/app-components/StyledButton';
import Label from 'src/components/Label';
import { FORM_TYPE, REQUEST_STATUS, SURVEY_TYPE } from 'src/constants';
import { CancelConfirmModal } from 'src/features/general/enquete-create/components/modals/cancel-confirm/components/CancelConfirmModal';
import { NotificateStateTypes } from 'src/features/general/enquete-create/pages/EnqueteCreatePage';
import { setEnqueteData } from 'src/features/general/enquete-create/store/enqueteCreateSlice';
import useLocales from 'src/hooks/useLocales';
import { usePermission } from 'src/hooks/usePermission';
import { RootState, useAppDispatch, useAppSelector } from 'src/redux/store';
import { conversionDateWithoutLocalHour, conversionUtcDate, dateToString, formatterDateTime, yearMonthFormat } from 'src/utils/formatDateTime';
import { getRequestStatus } from 'src/utils/getRequestStatus';
import { DuplicateWarnMessageModal } from '../../../modals/dupulicate-warn-message/components/DuplicateWarnMessageModal';
import { useEnqueteCreate } from '../hooks/useEnqueteCreate';
import useEnquetePageCommonGrid from '../hooks/useEnquetePageCommonGrid';

const EnquetePageCommonGrid: React.FC = () => {
  const { isJapanese } = useLocales();
  const { t } = useTranslation();
  const { getRequestStatusTerm, getDisclosureLevel, onClickExportCustomer } = useEnquetePageCommonGrid();
  const { isNewCreateMode } = useEnqueteCreate();
  const { isAdmin, isSurveyOwner } = usePermission();
  const dispatch = useAppDispatch();
  const enqueteData = useAppSelector((state: RootState) => state.enqueteCreate.enqueteData);
  const requestStatus = getRequestStatus(enqueteData.openedAt, enqueteData.closedAt);
  const linkState: unknown = useLocation().state;
  const [isOpenCancelConfirmModal, setIsOpenCancelConfirmModal] = useState(false);
  const [isDuplicateWarnMessageModalOpen, setIsDuplicateWarnMessageModalOpen] = useState(false);
  // memo: アンケート新規作成・編集モーダルの管理
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const isComplia = enqueteData.surveyDetail?.surveyHeader?.formType === FORM_TYPE.COMPLIA;
  const isGeneral = enqueteData.surveyDetail?.surveyHeader?.type === SURVEY_TYPE.GENERAL;
  const isOverview =
    enqueteData.surveyDetail?.surveyHeader?.type === SURVEY_TYPE.SPECIAL && enqueteData.surveyDetail?.surveyHeader?.formType === FORM_TYPE.OVERVIEW;
  const warnMessage =
    (isGeneral || isComplia) && REQUEST_STATUS.IN_PROGRESS === requestStatus
      ? t('enqueteCreate.warnMessage.inProgress')
      : isOverview && REQUEST_STATUS.IN_PROGRESS === requestStatus
      ? t('enqueteCreate.warnMessage.inOverviewProgress')
      : REQUEST_STATUS.COMPLETE === requestStatus
      ? t('enqueteCreate.warnMessage.complete')
      : undefined;

  const onDuplicateWarnMessageModalOpen = () => {
    setIsDuplicateWarnMessageModalOpen(true);
  };

  // duplicateWarnMessageが変更される想定はないので一度しか呼ばれない想定
  useEffect(() => {
    // 複製して遷移してきている、かつ、duplicateWarnMessageが存在している場合に複製結果警告モーダルを表示する
    // 複製タイミングではenqueteDataの初期化されたデータしか取得できないので、ここで実行しています。
    if (typeof linkState !== 'object' || linkState === null) return;
    const state = linkState as NotificateStateTypes;
    if (state.duplicated !== undefined && state.duplicated === true) {
      if (enqueteData.surveyDetail.duplicateWarnMessage) {
        setIsDuplicateWarnMessageModalOpen(true);
        state.duplicated = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueteData.surveyDetail.duplicateWarnMessage]);

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4} sx={{ whiteSpace: 'pre-line' }}>
          <Typography variant="caption">
            <Box sx={{ fontWeight: 'bold' }}>{t('enqueteCreate.enqueteLabel')}</Box>
          </Typography>
          <Typography
            variant="body1"
            children={isJapanese ? enqueteData.surveyDetail.surveyHeader?.labelJpn ?? '' : enqueteData.surveyDetail.surveyHeader?.labelEng ?? ''}
          />
        </Grid>
        <Grid item xs={12} md={4} sx={{ whiteSpace: 'pre-line' }}>
          <Typography variant="caption">
            <Box sx={{ fontWeight: 'bold' }}>{t('enqueteCreate.enqueteTitle')}</Box>
          </Typography>
          <Typography variant="body1" children={isJapanese ? enqueteData.titleJpn : enqueteData.titleEng} />
        </Grid>
        <Grid item xs={12} md={2} sx={{ whiteSpace: 'pre-line' }}>
          <Typography variant="caption">
            <Box sx={{ fontWeight: 'bold' }}>{t('common.organizationBaseDate')}</Box>
          </Typography>
          <Typography
            variant="body1"
            children={
              enqueteData?.organizationBaseDate
                ? formatterDateTime(
                    conversionUtcDate(dateToString(conversionDateWithoutLocalHour(enqueteData?.organizationBaseDate))),
                    yearMonthFormat,
                  )
                : ''
            }
          />
        </Grid>
        {enqueteData.surveyDetail.duplicateWarnMessage && (
          <Grid item xs={12} md={2} sx={{ whiteSpace: 'pre-line' }}>
            <StyledButton onClick={onDuplicateWarnMessageModalOpen}>{t('button.duplicateResult')}</StyledButton>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4} sx={{ whiteSpace: 'pre-line' }}>
          <Typography variant="caption">
            <Box sx={{ fontWeight: 'bold' }}>{t('enqueteCreate.enqueteRequestTermTitle')}</Box>
          </Typography>
          <Typography variant="body1" children={getRequestStatusTerm(enqueteData)} />
        </Grid>
        <Grid item xs={12} md={2} sx={{ whiteSpace: 'pre-line' }}>
          <Typography variant="caption">
            <Box sx={{ fontWeight: 'bold' }}>{t('enqueteCreate.enqueteTargetDate')}</Box>
          </Typography>
          <Typography
            variant="body1"
            children={
              enqueteData.surveyDetail?.surveyHeader?.formType === FORM_TYPE.COMPLIA
                ? formatterDateTime(enqueteData.openedAt ?? '-', yearMonthFormat)
                : formatterDateTime(enqueteData.targetDate ?? '-', yearMonthFormat)
            }
          />
        </Grid>
        <Grid item xs={12} md={2} sx={{ whiteSpace: 'pre-line' }}>
          <Typography variant="caption">
            <Box sx={{ fontWeight: 'bold' }}>{t('enqueteGeneralCommon.disclosureLevel')}</Box>
          </Typography>
          <Typography variant="body1" children={getDisclosureLevel(enqueteData)} />
        </Grid>
        <Grid item xs={12} md={2} sx={{ whiteSpace: 'pre-line' }}>
          <Typography variant="caption">
            <Box sx={{ fontWeight: 'bold' }}>{t('enqueteCreate.enqueteDataUpdateTitle')}</Box>
          </Typography>
          <Typography
            variant="body1"
            children={!isNewCreateMode && enqueteData.surveyDetail?.updatedAt ? formatterDateTime(enqueteData.surveyDetail.updatedAt.toString()) : ''}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Box
            sx={{
              alignItems: 'flex-start',
              display: 'flex',
              flexDirection: 'row',
            }}>
            <StyledButton onClick={() => setIsModalOpen(true)}>{t('button.edit')}</StyledButton>
            {(isGeneral || isComplia || isOverview) && requestStatus === REQUEST_STATUS.IN_PROGRESS && (
              <StyledButton onClick={() => setIsOpenCancelConfirmModal(true)} color="error" sx={{ marginLeft: '10px' }}>
                {t('enqueteCreate.button.cancel')}
              </StyledButton>
            )}
            {enqueteData.surveyDetail?.surveyHeader?.formType === FORM_TYPE.SERIES && (
              <StyledButton
                variant="outlined"
                sx={{ marginLeft: '1em' }}
                isDisabled={!isAdmin && !isSurveyOwner}
                onClick={() => {
                  onClickExportCustomer();
                }}>
                {t('enqueteCreate.button.customer')}
              </StyledButton>
            )}
          </Box>
        </Grid>
      </Grid>
      {warnMessage && (
        <Label role="message" color="warning" variant="ghost" sx={{ whiteSpace: 'pre-line', height: 'auto', lineHeight: 'normal', p: 1, mb: 2 }}>
          {warnMessage}
        </Label>
      )}
      <CancelConfirmModal
        isOpen={isOpenCancelConfirmModal}
        onClose={(entity?: RespondentRequestEntity) => {
          // memo:
          //   DB更新後の状態を反映し、画面を再描画
          //   undefined の場合は、更新しなかった or 更新失敗のため、 set しない
          if (entity !== undefined) {
            dispatch(
              setEnqueteData({
                ...entity,
                surveyDetail: enqueteData.surveyDetail,
              }),
            );
          }
          setIsOpenCancelConfirmModal(false);
        }}
        respondentRequestId={enqueteData.id}
      />
      {/** アンケート作成・編集モーダル */}
      <SurveyInformationModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        isNewCreateMode={isNewCreateMode}
        modalTitle={t('enqueteCreate.editEnqueteModal.title')}
      />
      {/* 複製結果ダイアログ */}
      <DuplicateWarnMessageModal isOpen={isDuplicateWarnMessageModalOpen} setIsDuplicateWarnMessageModalOpen={setIsDuplicateWarnMessageModalOpen} />
    </>
  );
};

export default EnquetePageCommonGrid;
